import { Heart } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import { cn } from '@/shared/utils/cn';

import { COURSE_CARD_CORNER_BASE } from '../lib/course-card-corner-control';

const FAVORITE_BURST_PARTICLE_COUNT = 7;
type FavoriteBurstVariant = 'color' | 'muted';

interface CourseCardFavoriteButtonProps {
  isFavorite: boolean;
  className?: string;
  onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
}

export function CourseCardFavoriteButton({
  isFavorite,
  className,
  onToggle,
}: CourseCardFavoriteButtonProps) {
  const [burstKey, setBurstKey] = useState(0);
  const [burstVariant, setBurstVariant] = useState<FavoriteBurstVariant>('color');
  const burstTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (burstTimer.current) window.clearTimeout(burstTimer.current);
    },
    [],
  );

  const handleFavoriteClick = (event: MouseEvent<HTMLButtonElement>) => {
    setBurstVariant(isFavorite ? 'muted' : 'color');
    setBurstKey((currentKey) => currentKey + 1);

    if (burstTimer.current) window.clearTimeout(burstTimer.current);
    burstTimer.current = window.setTimeout(() => {
      setBurstKey(0);
      burstTimer.current = null;
    }, 620);

    onToggle(event);
  };

  return (
    <button
      aria-label={isFavorite ? 'Quitar de favoritos' : 'Guardar en favoritos'}
      className={cn(
        COURSE_CARD_CORNER_BASE,
        'focus-visible:ring-(--active-magenta) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        isFavorite
          ? 'border-white/80 bg-white/95 text-(--active-magenta)'
          : 'border-white/80 bg-white/95 text-navy/55 hover:text-(--active-magenta)',
        className,
      )}
      type="button"
      onClick={handleFavoriteClick}
    >
      {burstKey > 0 ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-20"
          key={burstKey}
        >
          {Array.from({ length: FAVORITE_BURST_PARTICLE_COUNT }, (_, index) => (
            <span
              className={cn(
                'favorite-burst-particle',
                burstVariant === 'muted' && 'favorite-burst-particle-muted',
              )}
              key={index}
            />
          ))}
        </span>
      ) : null}
      <Heart
        aria-hidden="true"
        className={cn('relative z-10 h-3.5 w-3.5', isFavorite && 'fill-(--active-magenta)')}
        strokeWidth={2.4}
      />
    </button>
  );
}
